import {
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
  Timestamp,
} from 'firebase/firestore'
import { db } from 'config/firebase'
import { shipmentPlanConverter, transportTruckConverter } from '@otw/models/converters'
import {
  deriveShipmentLegsFromLocations,
  type LegFile,
  type ShipmentLeg,
  type ShipmentPlan,
  type ShipmentPlanLocation,
} from '@otw/models/logistics'
import {
  normalizeLeadTruckDropoffLocationIds,
  type Lead,
} from '@otw/models/leads/lead'

const TRANSPORT_TRUCKS_COLLECTION = 'transport_trucks'
const SHIPMENT_PLANS_COLLECTION = 'shipment_plans'

function getLatestShipmentPlan(plans: ShipmentPlan[]): ShipmentPlan | null {
  if (plans.length === 0) return null
  return [...plans].sort(
    (a, b) => (b.created_at?.toMillis?.() ?? 0) - (a.created_at?.toMillis?.() ?? 0),
  )[0]
}

function isDropoffAlreadyLastLocation(
  locations: ShipmentPlanLocation[],
  dropoffLocationId: string,
): boolean {
  const last = locations[locations.length - 1]
  return last?.location_type === 'pickup' && last?.location_id === dropoffLocationId
}

function locationsToFirestore(locations: ShipmentPlanLocation[]) {
  return locations.map((loc) => ({
    location_id: loc.location_id ?? '',
    location_type: loc.location_type ?? 'pickup',
    notes: loc.notes ?? null,
  }))
}

function legsToFirestore(legs: ShipmentLeg[]) {
  return legs.map((leg) => ({
    from_id: leg.from_id ?? '',
    from_type: leg.from_type ?? 'pickup',
    to_id: leg.to_id ?? '',
    to_type: leg.to_type ?? 'pickup',
    company_id: leg.company_id ?? null,
    company_history: (leg.company_history ?? []).map((h) => ({
      company_id: h.company_id ?? '',
      timestamp: h.timestamp ?? Timestamp.now(),
    })),
    driver_id: leg.driver_id ?? null,
    assignment_notes: leg.assignment_notes ?? null,
    assignment_notes_photo: leg.assignment_notes_photo
      ? {
          fileName: leg.assignment_notes_photo.fileName ?? '',
          storagePath: leg.assignment_notes_photo.storagePath ?? '',
          url: leg.assignment_notes_photo.url ?? '',
          fileSize: leg.assignment_notes_photo.fileSize ?? 0,
          fileType: leg.assignment_notes_photo.fileType ?? '',
          uploadedAt: leg.assignment_notes_photo.uploadedAt ?? Timestamp.now(),
          uploadedBy: leg.assignment_notes_photo.uploadedBy ?? '',
        }
      : null,
    dispatch_info: leg.dispatch_info ?? null,
    estimated_shipping_price: leg.estimated_shipping_price ?? null,
    ready_date: leg.ready_date ?? null,
    ready: leg.ready ?? null,
    status_history: (leg.status_history ?? []).map((h) => ({
      status: h.status ?? 'Draft',
      updated_at: h.updated_at ?? Timestamp.now(),
      notes: h.notes ?? null,
    })),
    gate_passes: (leg.gate_passes ?? []).map((f: LegFile) => ({
      fileName: f.fileName ?? '',
      storagePath: f.storagePath ?? '',
      url: f.url ?? '',
      fileSize: f.fileSize ?? 0,
      fileType: f.fileType ?? '',
      uploadedAt: f.uploadedAt ?? Timestamp.now(),
      uploadedBy: f.uploadedBy ?? '',
    })),
  }))
}

/**
 * When a lead is closed, append each linked truck's dropoff to the latest shipment plan
 * for the matching transport truck (if a plan already exists).
 */
export async function syncLeadDropoffToShipmentPlans(lead: Lead): Promise<void> {
  const truckIds = lead.truckIds ?? []
  if (truckIds.length === 0) return

  const truckDropoffs = normalizeLeadTruckDropoffLocationIds(lead.truckDropoffLocationIds)
  const trucksCollection = collection(db, TRANSPORT_TRUCKS_COLLECTION).withConverter(
    transportTruckConverter,
  )
  const plansCollection = collection(db, SHIPMENT_PLANS_COLLECTION).withConverter(
    shipmentPlanConverter,
  )

  for (const truckId of truckIds) {
    const dropoffLocationId = truckDropoffs[truckId]?.trim()
    if (!dropoffLocationId) continue

    const trucksSnap = await getDocs(
      query(trucksCollection, where('vehicle_id', '==', truckId)),
    )
    const transportTrucks = trucksSnap.docs.map((d) => d.data())
    if (transportTrucks.length === 0) continue

    for (const transportTruck of transportTrucks) {
      const plansSnap = await getDocs(
        query(plansCollection, where('transport_truck_id', '==', transportTruck.id)),
      )
      const plans = plansSnap.docs.map((d) => d.data())
      const latestPlan = getLatestShipmentPlan(plans)
      if (!latestPlan?.id) continue

      const existingLocations = latestPlan.locations ?? []
      if (isDropoffAlreadyLastLocation(existingLocations, dropoffLocationId)) continue

      const newLocations: ShipmentPlanLocation[] = [
        ...existingLocations,
        { location_id: dropoffLocationId, location_type: 'pickup' },
      ]
      const shipment_legs = deriveShipmentLegsFromLocations(
        newLocations,
        latestPlan.shipment_legs ?? [],
      )

      await updateDoc(doc(db, SHIPMENT_PLANS_COLLECTION, latestPlan.id), {
        updated_at: Timestamp.now(),
        locations: locationsToFirestore(newLocations),
        shipment_legs: legsToFirestore(shipment_legs),
      })
    }
  }
}
